import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import type { ShopProduct } from "@viajero/proto/guest/v1";
import { trackEvent } from "@viajero/shared-ui/analytics";
import { colors } from "@/theme/colors";

type Props = {
  caseToken: string;
  shopId: string;
  products: ShopProduct[];
};

/**
 * 店舗に紐づく商品 (メニュー) のカード一覧。画像・タイトル・金額・詳細ボタンを表示する。
 * 人数はこの一覧の元データ (ShopProduct) に含まれないため表示しない
 * (人数は GetProductDetail 側のみが持つ。JPTP_CANSEI-1256 の作業範囲外)。
 */
export function ShopMenuList({ caseToken, shopId, products }: Props) {
  const { t } = useTranslation();
  // RPC は成功したが商品が 0 件の場合。見出しだけが残って空白になるのを避ける (JPTP_CANSEI-1524)。
  if (products.length === 0) {
    return (
      <Typography sx={{ color: colors.textSub, fontSize: 14 }}>
        {t("shopDetail.menuEmpty")}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {products.map((product) => {
        const price = product.prices[0];
        return (
          <Card
            key={product.productId.toString()}
            variant="outlined"
            sx={{ borderColor: colors.border }}
          >
            <CardActionArea
              component={RouterLink}
              to={`/${caseToken}/${shopId}/${product.productId}`}
              onClick={() => trackEvent("click_shop_menu", { menu_name: product.name })}
              sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}
            >
              {product.imageUrl && (
                <CardMedia
                  component="img"
                  image={product.imageUrl}
                  alt={product.name}
                  sx={{ width: "100%", height: 160, objectFit: "cover", flexShrink: 0 }}
                />
              )}
              <CardContent sx={{ flex: 1, width: "100%" }}>
                <Typography sx={{ fontWeight: 600, color: colors.text, mb: 0.5 }}>
                  {product.name}
                </Typography>
                {price && (
                  <Typography sx={{ color: colors.textSub, fontSize: 13 }}>
                    ¥{price.price.toLocaleString()} {t("shopDetail.perPerson")}
                  </Typography>
                )}
                <Typography sx={{ color: colors.gold, fontSize: 13, fontWeight: 700, mt: 0.5 }}>
                  {t("shopDetail.viewDetails")}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}
